/**
 * Route-saga lifecycle for redux-first-router: spawns the matched route's init saga on
 * navigation and tears it down on exit.
 *
 * The route task is `spawn`ed, not `fork`ed, so it is NOT an attached child of this
 * takeLatest handler — takeLatest cancels the previous handler on every route action, and
 * an attached fork would die with it. Teardown is therefore explicit and deliberate, which
 * is what lets a route saga SURVIVE a navigation where both the outgoing and incoming
 * route set `preserveSagaOnRouteChange` and name the same saga function (VE-26729: seven
 * routes share initMainPage, and restarting it per switch refetched the entire media
 * list). Preservation is opt-in per route, never inferred from saga identity alone — see
 * the comment on the guard below for why the editor routes must be excluded. Spawning
 * also detaches the task from the root keepAlive wrapper, so runRouteSaga reports its own
 * crashes to Sentry instead of taking the routing watcher down with it.
 *
 * Teardown also narrowed in a second way worth knowing: takeLatest's implicit cancel used
 * to fire on EVERY route action, before any of the checks below. Now the early returns
 * (public standalone routes, requiresAuth denial, unchanged type+payload) all leave the
 * previous saga running.
 *
 * Consequence for route sagas: anything a page needs re-run on ENTRY (rather than once per
 * session) must be dispatched by the page itself, because a preserved saga's start-up
 * effects do not re-fire.
 */
import {
  select,
  take,
  all,
  fork,
  spawn,
  call,
  cancel,
  put,
  takeLatest,
} from 'typed-redux-saga/macro';
import { isEqual } from 'lodash';
import { redirect, Location } from 'redux-first-router';
import { modules } from '@veritone/glc-redux';
import * as Sentry from '@sentry/browser';

import { getPendo, getSegment } from '@helpers/analyticsHelper';

import {
  ROUTE_FORBIDDEN,
  selectRoutesMap,
  selectPreviousRoute,
  PUBLIC_STANDALONE_ROUTE_TYPES,
} from './index';
import { BOOT_FINISHED, bootDidFinish } from '../app';
import { Task } from 'redux-saga';
import { Routes } from '@redact/pages/routes';

const {
  user: { LOGOUT, userIsAuthenticated },
} = modules;

export default function* routes() {
  yield* all([
    fork(watchRouteSagas),
    fork(redirectToForbiddenRouteOnApiAuthErrors),
  ]);
}

type RouteSaga = () => Generator<any, void, unknown>;

// setup sagas on application boot
export let currentRouteTask: Task<any>;
// Which saga function currentRouteTask is running, so a navigation between two routes that
// declare the SAME saga can leave it alone instead of cancelling and re-spawning it.
export let currentRouteSaga: RouteSaga | undefined;

/**
 * Runs a route saga as a detached task, reporting a crash instead of letting it escape.
 *
 * Spawned tasks have no parent to propagate to, so without this an unhandled throw would
 * die silently. Deliberately does NOT restart the saga (unlike shared/keepSagasAlive): a
 * route saga that crashes on entry would otherwise retry in a hot loop. The page recovers
 * on the next navigation to it.
 */
export function* runRouteSaga(saga: RouteSaga) {
  try {
    yield* call(saga);
  } catch (e) {
    Sentry.captureException(
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      new Error(`route saga ${saga.name} aborted due to: ${e}`, { cause: e }),
      { tags: { saga: saga.name } }
    );
    console.error(`route saga ${saga.name} aborted due to:`, e);
  }
}

export function* watchRouteSagas() {
  const routesMap = yield* select(selectRoutesMap);

  // watch routing actions -- spawn route sagas when the route mounts, and
  // cancel them when the route exits.
  yield* takeLatest(Object.keys(routesMap), watchRouteSagasHandle(routesMap));
}
export const selectPendoSegment = (state: any) => {
  getPendo(state);
  getSegment(state, window.location.pathname);
};
export const watchRouteSagasHandle = (routesMap: Routes) =>
  function* (currentRoute: Location) {
    // Standalone public routes (e.g. the popped-out keyboard-shortcuts legend)
    // run no route sagas or analytics: getPendo assumes an authenticated user
    // and would throw when the window is opened while logged out.
    if (PUBLIC_STANDALONE_ROUTE_TYPES.includes(currentRoute.type)) {
      return;
    }

    const hasBooted = yield* select(bootDidFinish);
    if (!hasBooted) {
      yield* take(BOOT_FINISHED);
    }

    const userIsAuthed = yield* select(userIsAuthenticated);
    const currentRouteMap = routesMap[currentRoute.type];
    if (typeof currentRouteMap === 'object') {
      if (currentRouteMap?.requiresAuth && !userIsAuthed) {
        // do not run sagas for inaccessible routes
        return;
      }

      const previousRoute = yield* select(selectPreviousRoute);

      if (
        currentRoute.type === previousRoute.type &&
        isEqual(currentRoute.payload, previousRoute.payload)
        // todo: etc? query?
      ) {
        // no route change; leave sagas alone
        // fixme -- seems like this might not work. prev/current are never same?
        return;
      }

      /**
       * Poor getPendo() code failes if User not available. At least only inits once.
       * getSegment() does init and path hit. At least only inits once (sort of).
       */
      yield* select(selectPendoSegment);

      const nextRouteSaga = currentRouteMap.saga as RouteSaga | undefined;
      const previousRouteMap = routesMap[previousRoute.type];

      // VE-26729: the seven routes sharing initMainPage (Home, Settings Profile, Redaction
      // Codes, Statuses, Ingest, Ingest/GovQA, Trash) all set preserveSagaOnRouteChange.
      // Cancelling and re-spawning it on every switch refetched the whole media list, the
      // org tasks/jobs queries and the folder tree for pages that don't even show them.
      //
      // Saga identity alone is NOT sufficient to preserve, which is why both routes must
      // also opt in. A route re-entered with a DIFFERENT payload has the same saga
      // function and a still-running task, so an identity-only check would suppress its
      // re-initialization — and the editor routes rely on exactly that re-entry: a
      // case-less deep link injects the caseId and redirects to the same route type, trim
      // redirects to the newly created TDO, and "view redacted file" redirects to another
      // tdoId. Those must re-run their route saga or the editor never loads.
      if (
        nextRouteSaga &&
        nextRouteSaga === currentRouteSaga &&
        currentRouteMap.preserveSagaOnRouteChange &&
        typeof previousRouteMap === 'object' &&
        previousRouteMap?.preserveSagaOnRouteChange &&
        currentRouteTask?.isRunning()
      ) {
        return;
      }

      if (currentRouteTask) {
        yield* cancel(currentRouteTask);
      }
      currentRouteSaga = nextRouteSaga;

      if (nextRouteSaga) {
        currentRouteTask = yield* spawn(runRouteSaga, nextRouteSaga);
      }
    }
  };

function* redirectToForbiddenRouteOnApiAuthErrors() {
  const forbiddenStatusCodes = [401, 403];

  yield* takeLatest(
    function ({
      payload,
    }: { payload?: { name: string; status: number } } = {}) {
      if (payload) {
        const { name, status } = payload;
        return name === 'ApiError' && forbiddenStatusCodes.includes(status);
      } else {
        return false;
      }
    } as any, // TODO: Fix this
    redirectToForbiddenHandle
  );
}

export function* redirectToForbiddenHandle() {
  // ignore if user is not logged in
  if (yield* select(userIsAuthenticated)) {
    yield* put(redirect(ROUTE_FORBIDDEN()));
  } else {
    yield* put({ type: LOGOUT });
  }
}
